import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { FileDocument } from '../../@types/item';
import ExtendableGroup from '../../components/extendable-group/extendable-group';
import { getItem } from '../../selectors/item';
import {
  DocumentList,
  DocumentListFormat,
  DocumentListItem,
  DocumentListTitle,
} from '../../styled-components/components/DocumentList';
import { H3, H5 } from '../../styled-components/components/Heading';
import { IconDownload } from '../../styled-components/components/Icon';
import { Stack, StackMedium } from '../../styled-components/components/Stack';

const getExtension = (document: FileDocument) => {
  switch (document.contentType) {
    case 'application/pdf':
      return 'PDF';
    default: {
      const formattedContentType =
        document.url && document.url.includes('.') ? document.url.split('.').pop() : '';
      return formattedContentType;
    }
  }
};

interface SingleDocumentProps {
  document: FileDocument;
}

const SingleDocument = ({ document }: SingleDocumentProps) => {
  if (!document.name || !document.url) {
    return null;
  }

  const extension = getExtension(document);

  return (
    <DocumentListItem>
      <DocumentListTitle
        href={document.url}
        title={document.name}
        target="_blank"
        rel="noopener noreferrer"
      >
        {document.name}
      </DocumentListTitle>
      <DocumentListFormat>
        {extension}
        <IconDownload />
      </DocumentListFormat>
    </DocumentListItem>
  );
};

interface DocumentCategory {
  category: string;
  sortOrder: number;
  documents: FileDocument[];
}

const Documents = () => {
  const { t } = useTranslation(['item']);

  const Translation = {
    'tyre-label': t('documentTypes.tyreEnergyDeclaration', { ns: 'item' }),
    'content declaration': t('documentTypes.goodsDeclaration', { ns: 'item' }),
    other: t('documentTypes.other', { ns: 'item' }),
  };

  const SortOrder = {
    'tyre-label': 2,
    'content declaration': 1,
    other: 3,
  };

  type CategoryType = keyof typeof Translation;

  const documents = useSelector(getItem)?.documents || [];
  const categorised = documents
    .reduce((prev, curr) => {
      const index = prev.findIndex((x) => x.category === (curr.category || 'others'));
      if (index > -1) {
        prev[index].documents.push(curr);
      } else {
        const category = curr.category || 'others';
        prev.push({
          category,
          documents: [curr],
          sortOrder: SortOrder?.[category as CategoryType] ?? 99,
        });
      }

      return prev;
    }, [] as DocumentCategory[])
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (!documents.length) return null;

  const manyCategories = categorised.length > 1;

  return (
    <div className="item-section">
      <StackMedium>
        <H3 as="h2" $margin="none">
          {t('Documents', { ns: 'item' })}
        </H3>
      </StackMedium>
      <StackMedium>
        <ExtendableGroup label="Visa alla dokument" extended={documents.length < 5}>
          {categorised.map((group) => (
            <Stack key={group.category}>
              {manyCategories && (
                <H5 as="h3">
                  {Translation?.[group.category as CategoryType] || Translation.other}
                </H5>
              )}
              <DocumentList>
                {group.documents.map((document, index) => (
                  <SingleDocument document={document} key={`${group.category}-${index}`} />
                ))}
              </DocumentList>
            </Stack>
          ))}
        </ExtendableGroup>
      </StackMedium>
    </div>
  );
};

export default Documents;
